import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Table,
  message,
  Spin,
  Switch,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
} from "@ant-design/icons";
import assetCategoryService from "../../services/asset-category-service.ts";
import { useDispatch } from "react-redux";
import { addNotification }  from "../../redux/globalSlice.ts"; 



const AssetCategory = () => {
  const [form] = Form.useForm();
  const [visibleModal, setVisibleModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [status, setStatus] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);


const dispatch = useDispatch();


const fetchCategories = async (page = currentPage, size = pageSize) => {
  try {
    setLoading(true); 
    const res = await assetCategoryService.getAllCategories(page, size);

    setCategories(
      res.data.map((cat) => ({
        ...cat,
        key: cat.CategoryID,
      }))
    );

    setTotalItems(res.total);
    setCurrentPage(res.currentPage);
    setPageSize(res.perPage);
  } catch (error) {
    message.error("Failed to fetch categories");
  } finally {
    setLoading(false);
  }
};
/* eslint-disable react-hooks/exhaustive-deps */

useEffect(() => {
  fetchCategories(1, pageSize);
}, []);



  const handleOpenModal = (category = null) => {
    setSelectedCategory(category);
    setStatus(category?.Status === 1);
    form.setFieldsValue({
      categoryName: category?.CategoryName || "",
    });
    setVisibleModal(true);
  };



const paginationConfig =
  totalItems > 10
    ? {
        current: currentPage,
        pageSize: pageSize,
        total: totalItems,
        showSizeChanger: true,
        onChange: (page, size) => {
          fetchCategories(page, size);
        },
      }
    : false;


const handleSaveCategory = async () => {
  try {
    const values = await form.validateFields();

    const payload = {
      CategoryName: values.categoryName,
      Status: status ? 1 : 0,
    };

    let response;
    if (selectedCategory) {
      response = await assetCategoryService.updateCategory({
        ...payload,
        CategoryID: selectedCategory.CategoryID,
      });
    } else {
      response = await assetCategoryService.createCategory(payload);
    }

    dispatch(
      addNotification({
        title: "Success",
        description: response?.message || "Operation successful",
        type: "success",
      })
    );

    setVisibleModal(false);
    form.resetFields();
    setSelectedCategory(null);
    fetchCategories();
  } catch (error) {
    dispatch(
      addNotification({
        title: "Error",
        description:
          error?.response?.data?.error || error.message || "Failed to save category",
        type: "error",
      })
    );
  }
};



const handleDeleteCategory = async (id) => {
  try {
    const response = await assetCategoryService.deleteCategory(id);

    dispatch(
      addNotification({
        title: "Success",
        description: response?.message || "Category deleted successfully",
        type: "success",
      })
    );

    fetchCategories();
  } catch (error) {
    dispatch(
      addNotification({
        title: "Error",
        description:
          error?.response?.data?.error || error.message || "Failed to delete category",
        type: "error",
      })
    );
  }
};


  const columns = [
    {
      title: "Sl. No",
      render: (_: any, __: any, index: number) => index + 1,
      width: 100,
    },
    {
      title: "Category Name",
      dataIndex: "CategoryName",
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (val: number) =>
        val === 1 ? (
          <span style={{ color: "green" }}>Active</span>
        ) : (
          <span style={{ color: "red" }}>Inactive</span>
        ),
    },
      {
        title: "Actions",
        key: "actions",
        width: 100,
        render: (_, record) => (
          <div style={{ display: "flex", gap: "12px" }}>
            <span onClick={() => handleOpenModal(record)} style={{ cursor: "pointer" }}>
              <EditOutlined />
            </span>
          </div>
        ),
      }


  ];


return (
  <div className="vm__container vm__h-100">
    <div className="vm__d-flex vm__align-items-center vm__mb-3">
      <h3 className="vm__page-header" style={{ marginBottom: 0, flex: 1 }}>
        Asset Categories
      </h3>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => handleOpenModal()}
      >
        Add Category
      </Button>
    </div>

    {loading ? (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" />
      </div>
          ) : (
      <Table
        className="vm__table-container"
        dataSource={categories}
        columns={columns}
        rowKey="key"
        scroll={{ x: true }}
        pagination={paginationConfig}
      />

          )}

    <Modal
      title={selectedCategory ? "Edit Category" : "Add Category"}
      open={visibleModal}
      onCancel={() => {
        setVisibleModal(false);
        form.resetFields();
        setSelectedCategory(null);
      }}
      onOk={handleSaveCategory}
      okText="Save"
      maskClosable={false}
      width={500}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="categoryName"
          label="Category Name"
          rules={[{ required: true, message: "Please enter category name" }]}
        >
          <Input placeholder="Enter category name" />
        </Form.Item>

        <Form.Item label="Status">
          <Switch
            checked={status}
            onChange={(checked) => setStatus(checked)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </Form.Item>
      </Form>
    </Modal>
    <Modal
  title="Confirm Deletion"
  open={deleteModalVisible}
  onCancel={() => {
    setDeleteModalVisible(false);
    setDeleteCategoryId(null);
  }}
  onOk={async () => {
    if (deleteCategoryId) {
      await handleDeleteCategory(deleteCategoryId);
      setDeleteModalVisible(false);
      setDeleteCategoryId(null);
    }
  }}
  okText="Yes"
  cancelText="Cancel"
  maskClosable={false}
>
  <p>Are you sure you want to delete this category?</p>
</Modal>
  </div>
);



};

export default AssetCategory;
